import { apiClient } from './api/client';
import { Invoice, CreateInvoiceDto, UpdateInvoiceDto, InvoiceFilters } from '@/types/invoice.types';

export const invoiceService = {
  async getAll(filters?: InvoiceFilters): Promise<Invoice[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    const queryString = params.toString();
    const url = `/invoices${queryString ? `?${queryString}` : ''}`;
    return await apiClient.get<Invoice[]>(url);
  },

  async getById(id: number): Promise<Invoice> {
    return await apiClient.get<Invoice>(`/invoices/${id}`);
  },

  async create(data: CreateInvoiceDto): Promise<Invoice> {
    return await apiClient.post<Invoice>('/invoices', data);
  },

  async createFromOrder(orderId: number): Promise<Invoice> {
    return await apiClient.post<Invoice>('/invoices/from-order', { order_id: orderId });
  },

  async getByOrderId(orderId: number): Promise<Invoice | null> {
    try {
      return await apiClient.get<Invoice>(`/invoices/by-order/${orderId}`);
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  async update(id: number, data: UpdateInvoiceDto): Promise<Invoice> {
    return await apiClient.put<Invoice>(`/invoices/${id}`, data);
  },

  async updateStatus(id: number, status: Invoice['status']): Promise<Invoice> {
    return await apiClient.patch<Invoice>(`/invoices/${id}/status`, { status });
  },

  async duplicate(id: number): Promise<Invoice> {
    return await apiClient.post<Invoice>(`/invoices/${id}/duplicate`);
  },

  async delete(id: number): Promise<void> {
    return await apiClient.delete(`/invoices/${id}`);
  },

  async generatePDF(id: number): Promise<Blob> {
    const response = await fetch(`/api/invoices/${id}/pdf`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept': 'application/pdf',
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = 'Erreur lors de la génération du PDF';
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }
    
    // Vérifier que le Content-Type est bien application/pdf
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/pdf')) {
      throw new Error('Le serveur n\'a pas retourné un PDF valide');
    }
    
    return await response.blob();
  },
};
